import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { localize } from '@saviynt/common';
import {
  Badge,
  ButtonSelect,
  Checkbox,
  Icon,
  Label,
  Menu,
  TabContent,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { COL_FILTERS_MODEL } from '../../../models/CertsModels';
import {
  setColsFiltersForCerts,
  setIsBulkActionsCompleted,
  setPersonalizationStatus,
  setPersonalizationViewOption,
  updatePersonalizationAll,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  SCREEN_SIZE_TABLET_MEDIUM,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey, scrollToPosition } from '../../../utils/utils';
import {
  checkAllPersonalizedColumnsExist,
  setPersonalizedCols,
} from '../RowsFilter/utils';

import CertColsSortableItem from './CertColsSortableItem/CertColsSortableItem';
import {
  FIRST_TIME_BADGE_DISABLED,
  FIXED_COL_FILTERS,
  PRIORITY_COLUMNS,
} from './constant';
import { invertedMessages } from './messages';
import { getActiveFiltersForCols, sanitizeColFilters } from './utils';

import './ColumnsFilter.css';

const MIN_COUNT_FOR_TOOLTIP = 30;
const TOOLTIP_DELAY_IN_MS_TILE = 500;

const eoSortOrder = ['ENTITLEMENT', 'ACCOUNT', 'CHILD ENTITLEMENT'];

const sortOrder = ['USER', 'ACCOUNT', 'ENTITLEMENT', 'ROLE'];

//  Sort top-level items  order
const sortEoLineItems = (a, b) =>
  eoSortOrder.indexOf(a.value) - eoSortOrder.indexOf(b.value);
const sortLineItems = (a, b) =>
  sortOrder.indexOf(a.value) - sortOrder.indexOf(b.value);

function ColumnsFilter({
  isInMore,
  colFilters,
  setColFilters,
  setAppliedColFilters,
  appliedColFilters,
  isButtonKindIcon,
  setIsDraggingDndItem,
}) {
  const [showbadgeOnColButton, setShowbadgeOnColButton] = useState(false);
  const [activeTab, setActiveTab] = useState('USER');
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggingItemIndex, setDraggingItemIndex] = useState(null);
  const [draggingItemScrollAmount, setDraggingItemScrollAmount] = useState(0);
  const menuRef = useRef(null);
  const dropDownBodyRef = useRef(null);
  const columnsRef = useRef([]);

  const dispatch = useDispatch();
  const intl = useIntl();

  const { messages } = useSelector((state) => state?.user);
  const {
    core: { isLoading, campaignType, configMap },
    global: { columnPickerList, mediaQueryValue, viewRowsList },
    personalization: {
      clearCurrentPersonalization,
      personalizedData,
      viewOption,
    },
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const activeFilterTab = colFilters.find((filter) => !filter.isDisabled);
  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;
  const personalizationViewOption = viewOption?.view;
  const personalizedColsFilters = personalizedData?.result?.info
    ?.defaultView?.[0]
    ? personalizedData.result.info.defaultView[0].colsFilters
    : undefined;
  const isDefaultView = personalizationViewOption === VIEW_OPTIONS.DEFAULT_VIEW;
  const tabletMediumQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_MEDIUM
  );
  const l = useInvertedLocalize(invertedMessages);
  const isTabletMediumWidth = tabletMediumQuery.value <= mediaQueryValue;
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  useEffect(() => {
    const isAllPersonalisedColumnsExists = checkAllPersonalizedColumnsExist(
      columnPickerList,
      personalizedColsFilters
    );

    if (isAllPersonalisedColumnsExists) {
      dispatch(
        setPersonalizationViewOption({
          view: VIEW_OPTIONS.MY_PERSONALIZED_VIEW,
          applied: true,
        })
      );
      dispatch(updatePersonalizationAll({ columns: true }));
    } else {
      dispatch(
        setPersonalizationViewOption({
          view: VIEW_OPTIONS.DEFAULT_VIEW,
          applied: false,
        })
      );
      dispatch(updatePersonalizationAll({ columns: false }));
    }
  }, [dispatch, columnPickerList, personalizedColsFilters]);

  useEffect(() => {
    const shouldClose = isTabletMediumWidth && menuRef?.current && !isInMore;

    if (shouldClose) menuRef.current.closeDropdown();
  }, [isTabletMediumWidth, isInMore]);

  const tempColFilters = useMemo(() => {
    if (!columnPickerList || !viewRowsList) return [];

    return sanitizeColFilters(
      columnPickerList,
      configMap?.NEW_COLUMN_ORDER_CONFIG !== '1', // config pass
      viewRowsList
    );
  }, [columnPickerList, viewRowsList, personalizationViewOption, configMap]);

  const activeColFilters = useMemo(
    () => getActiveFiltersForCols(tempColFilters),
    [tempColFilters]
  );

  useEffect(() => {
    // to reset the columnref array
    if (viewOption) {
      columnsRef.current = [];
    }
  }, [viewOption]);

  useEffect(() => {
    if (tempColFilters.length > 0) {
      setColFilters(tempColFilters);
      setAppliedColFilters(activeColFilters);
    }
  }, [tempColFilters, activeColFilters, setColFilters, setAppliedColFilters]);

  const onColumnsUpdate = useCallback(() => {
    if (columnPickerList && viewRowsList) {
      if (
        personalizedColsFilters &&
        personalizationViewOption === VIEW_OPTIONS.MY_PERSONALIZED_VIEW
      ) {
        if (
          checkAllPersonalizedColumnsExist(
            columnPickerList,
            personalizedColsFilters
          )
        ) {
          const tempArr = setPersonalizedCols(
            tempColFilters,
            personalizedColsFilters
          );

          dispatch(updatePersonalizationAll({ columns: true }));

          setColFilters(tempArr);
          setAppliedColFilters(getActiveFiltersForCols(tempColFilters));

          return;
        }

        dispatch(
          setPersonalizationViewOption({
            view: VIEW_OPTIONS.DEFAULT_VIEW,
            applied: false,
          })
        );
        dispatch(updatePersonalizationAll({ columns: false }));
      }

      if (configMap?.NEW_COLUMN_ORDER_CONFIG !== '1') {
        tempColFilters.forEach((filter) => {
          // eslint-disable-next-line no-param-reassign
          filter.options = filter.options.sort(
            (a, b) =>
              PRIORITY_COLUMNS.indexOf(b.value) -
              PRIORITY_COLUMNS.indexOf(a.value)
          );
        });
      }

      setColFilters(tempColFilters);
      setAppliedColFilters(getActiveFiltersForCols(tempColFilters));
    }
  }, [
    columnPickerList,
    viewRowsList,
    personalizedColsFilters,
    personalizationViewOption,
    tempColFilters,
    dispatch,
    setColFilters,
    setAppliedColFilters,
    configMap,
  ]);

  const mergeAndFlattenArraysByOrder = useCallback((data) => {
    const keyOrder = ['USER', 'ACCOUNT', 'ENTITLEMENT', 'ROLE'];

    return keyOrder.reduce(
      (merged, key) => (data[key] ? merged.concat(data[key]) : merged),
      []
    );
  }, []);

  const personalized = useMemo(
    () =>
      personalizedColsFilters
        ? mergeAndFlattenArraysByOrder(personalizedColsFilters)
        : [],
    [personalizedColsFilters, mergeAndFlattenArraysByOrder]
  );

  const applied = useMemo(
    () =>
      appliedColFilters ? mergeAndFlattenArraysByOrder(appliedColFilters) : [],
    [appliedColFilters, mergeAndFlattenArraysByOrder]
  );

  const isPersonalizedColumn = useCallback(() => {
    if (isDefaultView) return false;

    if (personalized?.length !== applied?.length) return false;

    for (let i = 0; i < personalized.length; i += 1) {
      if (personalized[i] !== applied[i]) return false;
    }

    return true;
  }, [isDefaultView, personalized, applied]);

  useEffect(() => {
    onColumnsUpdate();
  }, [
    columnPickerList,
    viewRowsList,
    personalizedColsFilters,
    personalizationViewOption,
    onColumnsUpdate,
  ]);

  useEffect(() => {
    if (clearCurrentPersonalization) onColumnsUpdate();
  }, [
    columnPickerList,
    viewRowsList,
    personalizedColsFilters,
    personalizationViewOption,
    clearCurrentPersonalization,
    onColumnsUpdate,
  ]);

  useEffect(() => {
    dispatch(setColsFiltersForCerts(appliedColFilters));
    setShowbadgeOnColButton(
      FIRST_TIME_BADGE_DISABLED.count > 0 || isPersonalizedColumn()
    );
  }, [dispatch, appliedColFilters, isPersonalizedColumn]);

  // Handlers
  const handleActiveTab = (value) => {
    if (value) {
      scrollToPosition(dropDownBodyRef, 0);
      setActiveTab(value);
    }
  };

  const handleChange = (optionValue) => {
    const modifiedFilters = colFilters.map((filter) => {
      const modifiedFilter = {
        ...filter,
        options: filter.options
          .map((option) => {
            if (option.value === optionValue) {
              if (columnsRef.current.includes(optionValue)) {
                const updatedArray = columnsRef.current.filter(
                  (item) => item !== optionValue
                );

                columnsRef.current = updatedArray;
              } else {
                columnsRef.current.push(optionValue);
              }
            }

            return option.value === optionValue
              ? { ...option, isChecked: !option.isChecked }
              : option;
          })
          .sort((a, b) => b.isChecked - a.isChecked),
      };

      return modifiedFilter;
    });

    setColFilters(modifiedFilters);
    setAppliedColFilters(getActiveFiltersForCols(modifiedFilters));

    if (columnsRef.current.length > 0) {
      FIRST_TIME_BADGE_DISABLED.count += 1;
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      // setShowbadgeOnColButton(true);
    } else {
      if (personalizedData) {
        setShowbadgeOnColButton(personalized?.length === applied?.length);
      }

      FIRST_TIME_BADGE_DISABLED.count = 0;
      dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
      // setShowbadgeOnColButton(true);
    }

    dispatch(setIsBulkActionsCompleted(false));
  };

  const reorderColFilterList = (srcIndex, destIndex, parentFilter) => {
    const reorderedList = colFilters.map((filter) => {
      if (filter.value === parentFilter) {
        const tempOptions = filter.options.filter(
          (indexOption) => !FIXED_COL_FILTERS.includes(indexOption.value)
        );
        const [removedOption] = tempOptions.splice(srcIndex, 1);
        const modifiedOptions = [
          ...filter.options.filter((indexOption) =>
            FIXED_COL_FILTERS.includes(indexOption.value)
          ),
          ...tempOptions.slice(0, destIndex),
          removedOption,
          ...tempOptions.slice(destIndex),
        ].sort((a, b) => b.isChecked - a.isChecked);

        return { ...filter, options: modifiedOptions };
      }

      return filter;
    });

    setColFilters(reorderedList);
    FIRST_TIME_BADGE_DISABLED.count += 1;
    setAppliedColFilters(getActiveFiltersForCols(reorderedList));
  };

  const findItemIndex = (itemId) => {
    let foundIndex = null;

    colFilters.some((filter) => {
      const filteredOptions = filter.options.filter(
        (opt) => !FIXED_COL_FILTERS.includes(opt.value)
      );
      const idx = filteredOptions.findIndex((opt) => opt.value === itemId);

      if (idx !== -1) {
        foundIndex = idx;

        return true;
      }

      return false;
    });

    return foundIndex;
  };

  const handleDragStart = ({ active }) => {
    const scrollAmount = dropDownBodyRef?.current?.scrollTop || 0;
    const index = findItemIndex(active.id);

    setDraggedItem(active.id);
    setDraggingItemScrollAmount(scrollAmount);
    setDraggingItemIndex(index);
    setIsDraggingDndItem(true);
  };

  const handleDragEnd = ({ active, over }) => {
    setIsDraggingDndItem(false);
    if (!over || active.id === over.id) return;

    let parentFilter;
    let srcIndex;
    let destIndex;

    colFilters.find((filter) => {
      const filteredOptions = filter.options.filter(
        (opt) => !FIXED_COL_FILTERS.includes(opt.value)
      );
      const activeIndex = filteredOptions.findIndex(
        (opt) => opt.value === active.id
      );

      if (activeIndex !== -1) {
        parentFilter = filter.value;
        srcIndex = activeIndex;
        destIndex = filteredOptions.findIndex((opt) => opt.value === over.id);

        return true;
      }

      return false;
    });

    if (parentFilter != null && srcIndex != null && destIndex != null) {
      reorderColFilterList(srcIndex, destIndex, parentFilter);
      dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      dispatch(setIsBulkActionsCompleted(false));
    }
  };

  const handleDragEndWrapper = (event) => {
    setDraggedItem(null);
    setDraggingItemIndex(null);
    setDraggingItemScrollAmount(0);
    setIsDraggingDndItem(false);
    handleDragEnd(event);
  };

  // Component Get functions
  const getTabGroups = () => (
    <TabGroup
      value={activeTab}
      onChange={handleActiveTab}
      className='ColumnsFilter-dropdownTabGroup'>
      {colFilters?.map((tab) => {
        if (!tab.isDisabled) {
          return (
            <TabItem
              key={tab.value}
              label={l(tab.label)}
              value={tab.value}
              size='medium'
            />
          );
        }

        return <span key={tab.value} />;
      })}
    </TabGroup>
  );

  const getTooltipText = (option) => {
    const optionLabel =
      messages[option?.label] ||
      localize(intl, getMessageKey(option?.label?.split('.')[0]));

    return optionLabel?.length > MIN_COUNT_FOR_TOOLTIP ? optionLabel : null;
  };

  const getTooltipTrigger = (option) => (
    <Typography
      kind='body2'
      className='ColumnsFilter-checkBoxText'
      color='--color-foreground-neutral-bold'
      lineLimit={1}>
      {messages[option?.label] ||
        localize(intl, getMessageKey(option?.label?.split('.')[0]))}
    </Typography>
  );

  const draggedOption = draggedItem
    ? colFilters
        .flatMap((filter) => filter.options)
        .find((o) => o.value === draggedItem)
    : null;

  const overlayStyle = {
    position: 'fixed',
    top:
      draggingItemIndex !== null
        ? draggingItemIndex * 40 + 120 - draggingItemScrollAmount
        : 0,
    left: 0,
    opacity: 1,
  };

  const getColumnsFilter = () => (
    <div className='ColumnsFilter-dropDownBody'>
      {getTabGroups()}
      <div ref={dropDownBodyRef} className='ColumnsFilter-dropDownScrollArea'>
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEndWrapper}>
          <Typography
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className='ColumnsFilter-bodyNote'>
            {l(
              'The number of visible attributes depends on the size of your browser and screen. Any attributes not shown are visible in the detailed view.'
            )}
          </Typography>
          <div className='ColumnsFilter-filterList'>
            {colFilters
              ?.sort(isEOCampaign ? sortEoLineItems : sortLineItems)
              ?.map((tab) => (
                <TabContent
                  value={activeTab}
                  tabValue={tab?.value}
                  key={tab?.value}>
                  <SortableContext
                    items={tab?.options
                      ?.filter(
                        (indexOption) =>
                          !FIXED_COL_FILTERS.includes(indexOption?.value)
                      )
                      .map((option) => option.value)}
                    strategy={verticalListSortingStrategy}>
                    <div className='ColumnsFilter-listContainer'>
                      {tab?.options
                        ?.filter(
                          (indexOption) =>
                            !FIXED_COL_FILTERS.includes(indexOption?.value)
                        )
                        .map((option) => (
                          <CertColsSortableItem
                            key={option.value}
                            option={option}
                            handleChange={handleChange}
                            getTooltipText={getTooltipText}
                            getTooltipTrigger={getTooltipTrigger}
                          />
                        ))}
                    </div>
                  </SortableContext>
                </TabContent>
              ))}
          </div>
          <DragOverlay style={overlayStyle}>
            {draggedOption ? (
              <div className='ColumnsFilter-subItems'>
                {draggedOption?.isChecked ? (
                  <div className='ColumnsFilter-dragger'>
                    <Tooltip
                      isPortalEnabled
                      text='Drag to Reorder'
                      trigger={
                        <Icon
                          kind='DragVertical'
                          color='neutral-1000'
                          size='smallMedium'
                        />
                      }
                    />
                  </div>
                ) : (
                  <span className='ColumnsFilter-draggerFiller' />
                )}
                <Label
                  htmlFor={draggedOption?.value}
                  className='ColumnsFilter-checkBox'>
                  <Checkbox
                    id='cols-filter-checkbox'
                    name={draggedOption?.value}
                    isChecked={draggedOption?.isChecked}
                    dataTestId='checkbox-no-hovereffect'
                  />
                  <Tooltip
                    trigger={getTooltipTrigger(draggedOption)}
                    openDelay={TOOLTIP_DELAY_IN_MS_TILE}
                    text={getTooltipText(draggedOption)}
                    maxWidth='20rem'
                  />
                </Label>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );

  if (isInMore) return <>{getColumnsFilter()}</>;

  return (
    <Menu
      ref={menuRef}
      CustomMenu={getColumnsFilter()}
      setIsExpanded={() => {
        scrollToPosition(dropDownBodyRef, 0);
        setActiveTab(activeFilterTab?.value ? activeFilterTab?.value : '0');
      }}
      crossAxisOffset={isTabletMediumWidth ? -8 : 0}
      placement='bottom-end'
      fallbackPlacements={['bottom-start', 'bottom']}
      trigger={
        <ButtonSelect
          kind={isButtonKindIcon ? 'rectangle' : 'icon'}
          label={l('Columns')}
          ariaLabel={l('Columns')}
          size='medium'
          BadgeComp={
            showbadgeOnColButton ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          prefixIcon={
            <Icon kind='Columns' color='neutral-600' size='smallMedium' />
          }
          isDisabled={
            colFilters?.length === 0 || activeTabCount === 0 || isLoading
          }
          isVisualCueEnabled={false}
          className='ColumnsFilter-button'
        />
      }
      className='ColumnsFilter'
    />
  );
}

ColumnsFilter.propTypes = {
  isInMore: PropTypes.bool,
  colFilters: COL_FILTERS_MODEL,
  setColFilters: PropTypes.func.isRequired,
  setAppliedColFilters: PropTypes.func.isRequired,
  appliedColFilters: PropTypes.shape({}),
  isButtonKindIcon: PropTypes.bool,
  setIsDraggingDndItem: PropTypes.func.isRequired,
};

ColumnsFilter.defaultProps = {
  isInMore: false,
  appliedColFilters: {},
  isButtonKindIcon: false,
  colFilters: [],
};

export default ColumnsFilter;
